import React from 'react';
import styled from 'styled-components';
import html2React from 'html-react-parser';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useParams } from 'react-router-dom';
import isString from 'lodash/isString';
import isObject from 'lodash/isObject';
import capitalize from 'lodash/capitalize';

import { handleRedirect } from 'hooks/useRedirect';
import FormFieldFactory from 'components/form-field-factory';
import FormGroup from 'components/form-group';
import SignaturePad from 'components/signature-pad';
import Button from 'components/button';
import LoadingScreen from 'components/full-screen-loader';
import FeedBackScreen from 'components/feedback-screen';
import { getValidationSchemaForField } from '../utils/field-validations';
import { GET_FORM_DATA, SUBMIT_FORM_DATA } from '../dal/forms';
import ThankYouPage from './thankyou-page';

function loadScript(url, success) {
  var script = document.createElement('script');
  script.src = url;
  var head = document.getElementsByTagName('head')[0];
  head.appendChild(script);
}

const Wrapper = styled.div`
  position: fixed;
  left: 0px;
  top: 0px;
  width: 100%;
  height: 100%;
  overflow-y: hidden;
  background-color: ${({ bg = '#fff' }) => bg};
  display: flex;
  flex-direction: column;
  .placeholder-footer {
    height: 158px;
  }
`;

const FormWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  width: 100%;
`;

const ControlWrapper = styled.div`
  margin-top: 5px;
  margin-bottom: 5px;
  width: 100%;
  input,
  textarea,
  select {
    background-color: #fff;
    border: 1px solid #ddd;
    border-radius: 6px;
    &:focus {
      border-color: #000;
    }
  }
`;

const Submit = styled(Button)`
  width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background-color: #000 !important;
`;

const Actions = styled.div`
  box-sizing: border-box;
  margin-top: 10px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  button {
    width: 270px;
    @media (max-width: 500px) {
      width: 100%;
    }
  }
`;

const SubmitWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px 20px;
  flex-shrink: 0;
  box-shadow: rgba(0, 0, 0, 0.2) 0px 10px 50px;
  background: #fff;
  width: 100%;
  box-sizing: border-box;
  z-index: 10;
  .captchaTest {
    display: none;
  }
  &.captcha-visible {
    .captchaTest {
      display: block;
    }
  }
  .captchaLabel {
    margin-bottom: 7px;
  }
`;

const SubmitError = styled.div`
  font-size: 0.81em;
  color: red;
  text-align: center;
`;

const INVALID_SUBMISSION_MESSAGE = 'Please fix the highlighted fields above.';
// The server re-reads the template on submit, so a field made required (or a signature added)
// after this page loaded can come back as an error for something this page doesn't show.
const FORM_CHANGED_MESSAGE = 'This form was updated. Please reload the page and try again.';

const complexErrorToString = (error, depth = 0, includeKeyName) => {
  if (!error || isString(error) || depth > 3) return error;
  if (Array.isArray(error)) {
    return error
      .map((err) => complexErrorToString(err, depth + 1))
      .filter((z) => z)
      .join(', ');
  }
  if (isObject(error)) {
    return Object.keys(error)
      .map(
        (z) =>
          `${includeKeyName ? `${capitalize(z)}: ` : ''}${complexErrorToString(
            error[z],
            depth + 1
          )}`
      )
      .join(', ');
  }

  return 'Please provide data';
};

const Form = ({
  subject,
  fields = [],
  body = '',
  signature,
  hasBodySignature,
  initialValues: formInitialValues = {},
  boardId,
  cardId,
  formId,
  propertyId,
  onSuccess,
  onPaymentRequired,
  onError,
  pageBg,
  setPageBg,
  enableCaptcha,
  paymentRequired,
  paymentAmount,
  hideSubmit,
  submitButtonText
}) => {
  const captchaWidget = React.useRef(null);
  const [captchaToken, setToken] = React.useState();
  const [showCaptcha, setShowCaptcha] = React.useState(false);

  const { initialValues, fieldsMap, validationSchema } = React.useMemo(() => {
    const fieldsMap = {};
    const initialValues = {};
    const formSchema = {};

    if (signature || hasBodySignature) {
      formSchema.signature = getValidationSchemaForField({ uuid: 'signature', type: 'signature' });
    }

    fields.forEach((field) => {
      fieldsMap[field.uuid] = field;
      initialValues[field.uuid] = formInitialValues[field.uuid];
      formSchema[field.uuid] = getValidationSchemaForField(field);
    });

    const validationSchema = yup.object().shape(formSchema);

    return { fieldsMap, initialValues, validationSchema };
  }, []);

  // A captcha token can only be verified once, so after the server rejects a submission the
  // visitor has to solve a fresh one before trying again.
  const resetCaptcha = () => {
    if (!enableCaptcha) return;
    setToken(undefined);
    setShowCaptcha(false);
    try {
      window.grecaptcha?.reset?.(captchaWidget.current);
    } catch (error) {
      // the widget may not have rendered yet; nothing to reset
    }
  };

  // The server re-checks required fields and answers `{ status: 'invalid', errors: { [uuid]:
  // message } }` when any are missing. Show those next to their fields instead of failing the page.
  const showServerErrors = (serverErrors = {}, { setErrors, setTouched, setSubmitting }) => {
    const touchedFields = Object.keys(serverErrors).reduce(
      (acc, uuid) => ({ ...acc, [uuid]: true }),
      {}
    );
    // setTouched would re-run client validation and replace the server's errors, so skip it
    setTouched({ ...touched, ...touchedFields }, false);
    setErrors(serverErrors);
    setSubmitting(false);
    resetCaptcha();
  };

  const submitForm = async (values, helpers) => {
    try {
      const data = await SUBMIT_FORM_DATA({
        data: {
          ...values,
          aid: cardId,
          bid: boardId,
          fid: formId,
          propertyId,
          captcha: captchaToken
        }
      });
      if (data.status === 'success') {
        onSuccess?.(data);
      } else if (data.status === 'payment-required') {
        onPaymentRequired?.(data);
      } else if (data?.status === 'invalid' && Object.keys(data.errors || {}).length > 0) {
        showServerErrors(data.errors, helpers);
      } else {
        onError?.();
      }
    } catch (error) {
      onError?.(error);
    }
  };

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    submitCount,
    validateForm
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values, helpers) => {
      if (enableCaptcha && !captchaToken) return setShowCaptcha(true);
      helpers.setSubmitting(true);
      submitForm(values, helpers);
    }
  });
  React.useEffect(() => {
    document.title = subject;
    const a = document.getElementsByClassName('u_body');
    const bg = a?.[0]?.style?.backgroundColor;
    bg && setPageBg(bg);
  }, [subject]);

  const elements = html2React(body, {
    replace: (domNode) => {
      if (domNode?.attribs?.['data-form-control']) {
        const uuid = domNode?.attribs?.['data-field-uuid'];
        const field = fieldsMap[uuid];
        return (
          <ControlWrapper>
            <FormGroup errorText={touched[uuid] && complexErrorToString(errors[uuid], 0, false)}>
              <FormFieldFactory
                name={uuid}
                value={values[uuid]}
                field={field}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </FormGroup>
          </ControlWrapper>
        );
      }
    },
    transform(reactNode, domNode, index) {
      if (
        domNode?.name === 'a' &&
        (domNode?.attribs?.['href'] || '').startsWith('https://calendly.com/')
      ) {
        return React.cloneElement(reactNode, {
          onClick: (event) => {
            event.preventDefault();
            event.stopPropagation();
            Calendly.initPopupWidget({ url: domNode?.attribs?.['href'] });
          }
        });
      }

      return reactNode;
    }
  });

  React.useEffect(() => {
    if (enableCaptcha) {
      window.portalFormsGrecaptcha = function () {
        captchaWidget.current = grecaptcha.render('portal_forms_grecaptcha', {
          sitekey: '6LcjyQAVAAAAANsQu1Ory3Q2mz2Y3fMwjKVFN8no',
          callback: (token) => {
            setToken(token);
            handleSubmit();
          }
        });
      };
      loadScript(
        'https://www.google.com/recaptcha/api.js?onload=portalFormsGrecaptcha&render=explicit'
      );
    }
  }, []);

  return (
    <>
      <FormWrapper bg={pageBg}>
        <div>{elements}</div>
        {!hasBodySignature && signature && (
          <Actions>
            <div>
              <FormGroup label="Signature" errorText={errors.signature}>
                <SignaturePad name="signature" value={values.signature} onChange={handleChange} />
              </FormGroup>
            </div>
          </Actions>
        )}
      </FormWrapper>
      {!hideSubmit && (
        <SubmitWrapper className={showCaptcha ? 'captcha-visible' : ''}>
          {enableCaptcha && (
            <>
              <div className="captchaTest">
                <div className="captchaLabel">Please resolve the captcha</div>
                <div id="portal_forms_grecaptcha" />
              </div>
            </>
          )}

          {submitCount > 0 && Object.keys(errors).length > 0 && (
            <SubmitError role="alert">
              {Object.keys(errors).some(
                (uuid) => !fieldsMap[uuid] && !(uuid === 'signature' && signature)
              )
                ? FORM_CHANGED_MESSAGE
                : INVALID_SUBMISSION_MESSAGE}
            </SubmitError>
          )}

          <Actions>
            {paymentRequired && (
              <Submit
                type="submit"
                variant="primary"
                disabled={isSubmitting ? true : showCaptcha}
                onClick={handleSubmit}>
                {isSubmitting ? 'PAYING...' : `PAY ${paymentAmount}`}
              </Submit>
            )}
            {!paymentRequired && (
              <Submit
                type="submit"
                variant="primary"
                disabled={isSubmitting ? true : showCaptcha}
                onClick={handleSubmit}>
                {isSubmitting ? 'SENDING...' : <>{submitButtonText || 'SUBMIT'}</>}
              </Submit>
            )}
          </Actions>
        </SubmitWrapper>
      )}
    </>
  );
};

export default () => {
  const [data, setData] = React.useState();
  const [error, setError] = React.useState(false);
  const [formSubmitted, setSubmitted] = React.useState(false);
  const { boardId, cardId, formId, action } = useParams();
  const [pageBg, setPageBg] = React.useState();
  const queryParameters = new URLSearchParams(window.location.search);
  const propertyId = queryParameters.get('uid');
  const enableThankYouPage = data?.thankYouPage && data?.enableThankyouPage !== false;

  const getForm = async () => {
    try {
      const data = await GET_FORM_DATA({
        cardId,
        boardId,
        formId
      });

      data.propertyId = propertyId;
      if (!data.boardId) setError(true);
      setData(data);
    } catch (error) {
      setError(true);
    }
  };

  React.useEffect(() => {
    getForm();
  }, []);

  React.useEffect(() => {
    if (data?.redirectOptions && ['pay-success', 'success'].includes(action)) {
      handleRedirect(data.redirectOptions);
    }
  }, [data, action]);

  if (error) return <FeedBackScreen variant="error" text="Oops!! Something went wrong 😔" />;
  if (!data) return <LoadingScreen text="LOADING FORM" />;
  if (formSubmitted || ['pay-success', 'success'].includes(action)) {
    return enableThankYouPage ? (
      <ThankYouPage html={data.thankYouPage} />
    ) : (
      <FeedBackScreen variant="success" text="Thank you, your data has been received!" />
    );
  }
  if (action === 'pay-cancel') {
    return (
      <FeedBackScreen
        variant="error"
        text={
          <div style={{ padding: '0 20px' }}>
            <h3>You've cancelled your transaction!</h3>
            <div>Please re-submit your information and complete the required payment.</div>
            <div>
              <a href={location.href.replace('pay-cancel', '')}>Continue</a>
            </div>
          </div>
        }
      />
    );
  }

  return (
    <Wrapper bg={pageBg}>
      <Form
        {...data}
        onSuccess={(data) => {
          handleRedirect(data.redirectOptions);
          setSubmitted(true);
        }}
        onPaymentRequired={(data) => {
          window.location = data.paymentUrl;
        }}
        onError={() => setError(true)}
        pageBg={pageBg}
        setPageBg={setPageBg}
      />
      {/* <div className="placeholder-footer" /> */}
    </Wrapper>
  );
};
